import { useGetAccountFlipperFeatureFlags } from '@libs/data-access/queries'
import { ConditionalWrap } from '@libs/ui/ds'
import { useTrackScheduleFeaturesInteraction } from '@spa/core/externals/segment'
import type { ScheduleFeatureShiftPlanificationType } from '@spa/core/externals/segment/methods/track/types'
import {
  contractsSelectors,
  shiftsSelectors,
  useOpenShiftModalPolicies,
  usePlanningPolicies,
  weeklyschedulesSelectors
} from '@spa/scenes/Plannings/redux/selectors'
import moment from 'moment'
import React from 'react'
import type { PropsWithChildren, ReactElement, FC } from 'react'

import {
  editShiftModalDef,
  realHoursModalDef,
  unvalidateHoursModalDef,
  unvalidateHoursModalLegacyDef
} from '../../../Modals'

import styles from './ShiftClick.module.css'

type Props = {
  shiftId: number
  weeklyscheduleId: number
  isRnd?: boolean // @todo remove this props drilling
}

function isPast(date: string) {
  return moment().isAfter(date, 'minute')
}
const ShiftClick: FC<PropsWithChildren<Props>> = ({
  shiftId,
  weeklyscheduleId,
  isRnd,
  children = null
}) => {
  const scheduleTracker = useTrackScheduleFeaturesInteraction()
  const accountFlipperFeatureFlags = useGetAccountFlipperFeatureFlags()
  /* Modals API */
  const { open } = editShiftModalDef.useDispatchModal()
  const { open: openRealHoursModal } = realHoursModalDef.useDispatchModal()
  const { open: openUnvalidateModal } =
    unvalidateHoursModalDef.useDispatchModal()
  const { open: openUnvalidateModalLegacy } =
    unvalidateHoursModalLegacyDef.useDispatchModal()

  const policies = usePlanningPolicies()
  const shift = shiftsSelectors.useGetCachedById(shiftId)
  const shiftWeeklyschedule = weeklyschedulesSelectors.useGetCachedById(
    shift.weeklyschedule_id
  )
  const shiftContract = contractsSelectors.useGetCachedById(
    shift.user_contract_id
  )
  const allowed = useOpenShiftModalPolicies({ shiftId, weeklyscheduleId })

  const handleClick = event => {
    event.preventDefault()

    const isValidated = Boolean(shift.validated_at)
    // can only edit real hours if you have a user_contract_id
    const shouldEditRealHours =
      shiftWeeklyschedule?.published &&
      isPast(shift.starts_at) &&
      Boolean(shift.user_contract_id)
    const shouldEditPlannedHours = policies.canEditShift

    // We return now to make sure unauthorized employees' click do not trigger a tracking action
    if (!(isValidated || shouldEditRealHours || shouldEditPlannedHours)) {
      return
    }

    scheduleTracker.scheduleElementCardOpened(
      'shift',
      shift.planification_type as ScheduleFeatureShiftPlanificationType,
      {
        published: shiftWeeklyschedule.published,
        validated: Boolean(shift.validated_at),
        declared: Boolean(shift.real_hours_declared_correct_at)
      }
    )

    switch (true) {
      case isValidated:
        return accountFlipperFeatureFlags.data?.scheduling_burning_redux
          ? openUnvalidateModal({
              restId: shift.rest_id,
              shiftId: shift.id,
              weeklyscheduleId: shift.weeklyschedule_id,
              membershipId: shiftContract?.membership_id
            })
          : openUnvalidateModalLegacy({
              restId: shift.rest_id,
              shiftId: shift.id,
              weeklyscheduleId: shift.weeklyschedule_id,
              membershipId: shiftContract?.membership_id
            })
      case shouldEditRealHours:
        return openRealHoursModal({
          shiftId: shift.id,
          weeklyscheduleId: shift.weeklyschedule_id,
          membershipId: shiftContract?.membership_id
        })
      case shouldEditPlannedHours:
        return open({
          shiftId,
          weeklyscheduleId: shift.weeklyschedule_id
        })
      default: // Do nothing
    }
  }

  if (isRnd) {
    return children as ReactElement
  }

  return (
    <ConditionalWrap
      condition={allowed}
      wrap={wrapChildren => (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div tabIndex={0} className={styles.Container} onClick={handleClick}>
          {wrapChildren}
        </div>
      )}
    >
      {children as ReactElement}
    </ConditionalWrap>
  )
}

ShiftClick.displayName = 'ShiftClick'

export default ShiftClick
